import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useConnection } from '@solana/wallet-adapter-react';
import {
  Transaction,
  SystemProgram,
  PublicKey,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import BalanceGate from '../components/wallet/BalanceGate';
import GameCanvas from '../components/game/GameCanvas';
import GameHUD from '../components/game/GameHUD';
import GameOverlay from '../components/game/GameOverlay';
import CelebrationOverlay from '../components/game/CelebrationOverlay';
import CreditsDisplay from '../components/game/CreditsDisplay';
import PurchaseModal from '../components/game/PurchaseModal';
import SeasonStateOverlay from '../components/game/SeasonStateOverlay';
import { useGameStore } from '../stores/gameStore';
import { useCreditsStore } from '../stores/creditsStore';
import { useCelebrationStore } from '../stores/celebrationStore';
import { useSeasonStore } from '../stores/seasonStore';
import { usePrizePool, formatPrizeUsd } from '../hooks/usePrizePool';
import { PHASE_CONFIG } from '@beat-games/game-config';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Format time remaining for display
function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return 'Phase Ending...';

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  // For testing mode (less than 1 hour), show mm:ss
  if (days === 0 && hours === 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // For production mode, show Xd Xh Xm
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(' ');
}

export default function Play() {
  const { phase, reset: resetGameStore } = useGameStore();
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { fetchCredits, purchaseCredits, isPurchasing } = useCreditsStore();
  const { clearCelebration } = useCelebrationStore();
  const { prizePoolUsd } = usePrizePool();
  const { phaseType: currentPhase, phaseName, timeRemainingMs } = useSeasonStore();
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  // Reset game state when component mounts (clears previous game results)
  useEffect(() => {
    resetGameStore();
    clearCelebration();
  }, [resetGameStore, clearCelebration]);

  // Eligibility/elimination state
  const [isEliminated, setIsEliminated] = useState(false);
  const [eliminatedFromPhase, setEliminatedFromPhase] = useState<string | undefined>();
  const [eliminationRank, setEliminationRank] = useState<number | undefined>();
  const [cutoffScore, setCutoffScore] = useState<number | undefined>();

  // Fetch eligibility info (includes elimination status)
  const fetchEligibility = useCallback(async () => {
    if (!publicKey) {
      setIsEliminated(false);
      setEliminatedFromPhase(undefined);
      setEliminationRank(undefined);
      setCutoffScore(undefined);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/eligibility?wallet=${publicKey.toBase58()}`);
      const data = await response.json();
      if (data.success && data.data) {
        // Check for both eliminated and not_qualified statuses
        const isPlayerEliminated = data.data.phaseStatus === 'eliminated';
        const isPlayerNotQualified = data.data.phaseStatus === 'not_qualified';

        setIsEliminated(isPlayerEliminated || isPlayerNotQualified);
        if (isPlayerEliminated) {
          setEliminatedFromPhase(data.data.eliminatedFromPhase);
          setEliminationRank(data.data.eliminationRank);
          setCutoffScore(data.data.cutoffScore);
        } else if (isPlayerNotQualified) {
          // For not qualified, we don't have elimination details
          // The reason is in data.data.reason
          setEliminatedFromPhase(undefined);
          setEliminationRank(undefined);
          setCutoffScore(undefined);
        }
      }
    } catch (error) {
      console.error('Failed to fetch eligibility:', error);
    }
  }, [publicKey]);

  // Fetch credits and eligibility when wallet connects
  useEffect(() => {
    if (publicKey) {
      fetchCredits(publicKey.toBase58());
      fetchEligibility();
    } else {
      // Reset elimination state when wallet disconnects
      setIsEliminated(false);
    }
  }, [publicKey, fetchCredits, fetchEligibility]);

  // Send SOL transfer to treasury wallet
  const sendSolTransfer = useCallback(
    async (treasuryAddress: string, solAmount: number): Promise<string> => {
      if (!publicKey || !sendTransaction) {
        throw new Error('Wallet not connected');
      }

      const treasuryPubkey = new PublicKey(treasuryAddress);
      const lamports = Math.round(solAmount * LAMPORTS_PER_SOL);

      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: treasuryPubkey,
          lamports,
        })
      );

      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      const signature = await sendTransaction(transaction, connection);

      // Wait for confirmation
      await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight,
      });

      // Small delay to ensure transaction is indexed by RPC
      await new Promise((resolve) => setTimeout(resolve, 2000));

      return signature;
    },
    [publicKey, sendTransaction, connection]
  );

  const handlePurchase = async (packs: number) => {
    if (!publicKey) return;

    const success = await purchaseCredits(publicKey.toBase58(), packs, sendSolTransfer);
    if (success) {
      setIsPurchaseModalOpen(false);
      // Clear error code so "Click to Start" shows instead of "No Credits"
      useGameStore.getState().setErrorCode(null);
    }
  };

  const phaseConfig = PHASE_CONFIG[currentPhase];

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center relative overflow-hidden bg-[#030303]">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Subtle atmosphere */}
      <div className="fixed inset-0">
        {/* Center glow - subtle focus on canvas area */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(255,255,255,0.02) 0%, transparent 70%)',
          }}
        />

        {/* Vignette - edge darkening */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_50%,transparent_0%,#030303_100%)]" />
      </div>

      {/* Season indicator + Timer + Credits display */}
      <div className="relative z-10 mb-4 flex flex-col sm:flex-row items-center gap-3 animate-slide-up-fade">
        <div className="flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff88] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff88]"></span>
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
            Season 1 · {phaseName}
          </span>
        </div>

        {/* Phase timer - show when we have timing info */}
        {timeRemainingMs !== null && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            timeRemainingMs <= 0
              ? 'bg-[#f59e0b]/10 border border-[#f59e0b]/20'
              : 'bg-white/[0.03] border border-white/[0.06]'
          }`}>
            <svg className={`w-3 h-3 ${timeRemainingMs <= 0 ? 'text-[#f59e0b]' : 'text-white/40'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className={`text-[10px] tracking-[0.15em] uppercase font-mono ${
              timeRemainingMs <= 0 ? 'text-[#f59e0b]' : 'text-white/60'
            }`}>
              {formatTimeRemaining(timeRemainingMs)}
            </span>
          </div>
        )}

        {/* Credits display - only show when wallet connected */}
        {publicKey && (
          <CreditsDisplay onPurchaseClick={() => setIsPurchaseModalOpen(true)} />
        )}
      </div>

      {/* Difficulty indicator for current phase */}
      {currentPhase !== 'QUALIFIER' && (
        <div className="relative z-10 mb-2 animate-slide-up-fade" style={{ animationDelay: '0.05s' }}>
          <div className="flex items-center gap-2 px-3 py-1 bg-[#f59e0b]/10 border border-[#f59e0b]/20 rounded-full">
            <svg className="w-3 h-3 text-[#f59e0b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#f59e0b]/80">
              Increased Difficulty · {phaseConfig.startRadius}px start
            </span>
          </div>
        </div>
      )}

      {/* Game Container */}
      <div className="relative z-10 w-full max-w-[min(800px,calc(100vw-2rem))] px-4 sm:px-0 py-20 sm:py-[4.5rem]">
        <BalanceGate>
          <div className={`relative ${phase === 'ended' ? 'animate-shake' : ''}`}>
            {/* Arena frame - outer glow */}
            <div className="absolute -inset-4 bg-white/[0.02] rounded-2xl blur-xl opacity-0 transition-opacity duration-500"
                 style={{ opacity: phase === 'playing' ? 0.5 : 0 }} />

            {/* Arena frame - border */}
            <div className={`absolute -inset-px rounded-xl transition-all duration-500 ${
              phase === 'playing'
                ? 'bg-gradient-to-b from-white/20 via-white/5 to-transparent'
                : 'bg-white/[0.06]'
            }`} />

            {/* Arena frame - corners */}
            <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-white/20 rounded-tl" />
            <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-white/20 rounded-tr" />
            <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-white/20 rounded-bl" />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-white/20 rounded-br" />

            {/* Game canvas with responsive wrapper */}
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-[#050505]">
              <GameCanvas onPurchaseClick={() => setIsPurchaseModalOpen(true)} />
            </div>

            {/* HUD */}
            <GameHUD />

            {/* Overlay (countdown, game over) */}
            <GameOverlay />

            {/* Celebration overlay (personal best, high score) */}
            <CelebrationOverlay />

            {/* Season state overlay (not started, closed, phase ending, eliminated) */}
            <SeasonStateOverlay
              currentPhase={currentPhase}
              timeRemainingMs={timeRemainingMs}
              phaseName={phaseName}
              isEliminated={isEliminated}
              eliminatedFromPhase={eliminatedFromPhase}
              eliminationRank={eliminationRank}
              cutoffScore={cutoffScore}
            />
          </div>
        </BalanceGate>
      </div>

      {/* Bottom info bar */}
      <div className="relative z-10 mt-6 animate-slide-up-fade" style={{ animationDelay: '0.1s' }}>
        <div className="flex items-center gap-6 text-[10px] tracking-[0.2em] uppercase text-white/30">
          <span>45 Seconds</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>Top 256 Qualify</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span className="text-[#00ff88] flex items-center gap-1.5">
            {prizePoolUsd ? formatPrizeUsd(prizePoolUsd) : '$—'} Prize
            <svg className="w-2.5 h-2.5 text-[#00ff88]/60 animate-bounce" style={{ animationDuration: '2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </span>
        </div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        onConfirm={handlePurchase}
        isLoading={isPurchasing}
      />
    </div>
  );
}
